import { Link } from 'react-router-dom'
import type { FirstWeekResponse, FirstWeekStepKey } from 'aeo-schema-generator-shared/types'
import { Steps } from '@/design-system/primitives'
// The shell is the system's panel card, drawn by hand because the head holds
// the deal line and the step bars rather than one sentence. The card's own
// stylesheet has to come with it.
import '@/design-system/appcards.css'
import '@/design-system/first-week.css'
import { FirstWeekStepRow } from './FirstWeekStepRow'
import { COLLAPSED_LINE, DISMISS, FIRST_WEEK_TITLE, FREE_LINE, MANUAL_FREE_LINE, STEP_COPY, endingSoonParts, liveStepKey, trialLine } from './firstWeekCopy'

const BILLING_PATH = '/settings/billing'
const ENDING_SOON_MS = 2 * 24 * 60 * 60 * 1000

/*
  The first-week guide. A panel card at the top of the dashboard: the deal in
  literal terms, the six-pixel step bars, and the steps as a ledger. It is a
  guest on the page: it never shows a spinner and never blocks anything.

  The deal line is only true while the trial is running. A customer who has
  converted still has an end date on record, and "nothing is charged before
  then" would be a lie to them, so the line is tied to the status, not the date.
*/
export function FirstWeekPanel({
  guide,
  onStep,
  onDismiss,
  ownerName,
}: {
  guide: FirstWeekResponse
  onStep: (key: FirstWeekStepKey) => void
  onDismiss: () => void
  ownerName?: string | null
}) {
  if (guide.dismissed) {
    return (
      <p className="ds-fw__collapsed" data-testid="first-week-collapsed">
        {COLLAPSED_LINE(guide.doneCount, guide.totalCount)}
      </p>
    )
  }

  const trialing = guide.trial?.status === 'trialing'
  const endsAt = guide.trial?.endsAt ?? null
  const endingSoon = trialing && !!endsAt && new Date(endsAt).getTime() - Date.now() <= ENDING_SOON_MS
  const currentIndex = guide.steps.findIndex((s) => s.key === guide.current)
  /*
    Which bar reads as "now". None when no step is current, which covers a
    finished guide and also a free account's teammate, who can have no
    current step while one only the owner can do is still waiting. Falling
    back to the first bar would point that teammate at the very step the
    server kept them away from.
  */
  const nowBar = currentIndex < 0 ? guide.steps.length : currentIndex
  /*
    On the free guide, adding the site is the owner's to do, and so is the
    report until the team has one: the read that makes it is the owner's to
    start. The row offers a teammate nothing on those. Once there is a
    report it is the team's, and a teammate may open it: they are the person
    asked to connect HubSpot and put the first page live from it.

    Putting the first page live is the same until there is somewhere to do
    it: the report, or the setup of a page made before there were reports.
    With neither, the step leads to the start page, which sends anybody who
    does not own the team straight back here. A button that goes nowhere is
    not offered.
  */
  const ownersOnly = (key: FirstWeekStepKey) => {
    if (guide.variant !== 'free' || guide.viewer !== 'teammate') return false
    if (key === 'add_site') return true
    if (key === 'report') return !guide.reportScanId
    if (key === 'free_page') return !guide.reportScanId && !guide.firstPageUrl
    return false
  }
  const ending = endingSoon && endsAt ? endingSoonParts(endsAt) : null

  return (
    <section className="ds-panel ds-fw" aria-labelledby="first-week-title">
      <div className="ds-panel__head ds-fw__head">
        <h3 className="ds-h3" id="first-week-title">
          {guide.viewer === 'teammate' && ownerName && guide.domain ? `${ownerName} asked you to set up ${guide.domain.domain}` : FIRST_WEEK_TITLE}
        </h3>
        {guide.trial && trialing && guide.viewer === 'owner' && (
          <p className="ds-fw__deal">
            {ending ? (
              <>
                {ending.before}
                <Link to={BILLING_PATH}>{ending.linkWord}</Link>
                {ending.after}
              </>
            ) : (
              trialLine(guide.trial.planName, endsAt)
            )}
          </p>
        )}
        {/* An account with no plan has a deal too, and it is not a date:
            what is free, and where a plan begins. Owners only, like the
            trial line: a teammate did not make this deal. */}
        {guide.variant === 'free' && guide.viewer === 'owner' && <p className="ds-fw__deal">{guide.installationMethod === 'manual' ? MANUAL_FREE_LINE : FREE_LINE}</p>}
        {/* Steps here are earned in any order, so each bar carries its own
            state. Position alone would paint a bar blue for a step nobody
            has done. */}
        <Steps
          steps={guide.steps.map((s) => STEP_COPY[s.key].doneLabel)}
          current={nowBar}
          done={guide.steps.map((s) => s.state === 'done')}
        />
      </div>
      <div className="ds-panel__body">
        <ol className="ds-fw__list" role="list">
          {guide.steps.map((step, i) => (
            <FirstWeekStepRow
              key={step.key}
              step={step}
              index={i}
              isCurrent={step.key === guide.current}
              inFlight={!!guide.inFlight && step.key === liveStepKey(guide.variant)}
              onAct={onStep}
              variant={guide.variant}
              installationMethod={guide.installationMethod}
              canAct={!ownersOnly(step.key)}
            />
          ))}
        </ol>
      </div>
      <div className="ds-panel__foot ds-fw__foot">
        <span />
        <button type="button" className="ds-fw__dismiss" onClick={onDismiss}>
          {DISMISS}
        </button>
      </div>
    </section>
  )
}
